import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * A chart, inlined into the page so its bars and slices can move and be pointed
 * at — in the flow of the question, without opening anything.
 *
 * This is the one thing that cannot be done with an `<img>`. An SVG served as an
 * image is a photograph: no pointer events reach it, `:hover` does not apply, and
 * a CSS animation inside it starts when the browser decides to. That last one is
 * why the figures ship still: the entrance animation was built, measured, and
 * turned off, because `both` leaves `opacity: 0` / `scaleY(0)` as the resting
 * state and a stalled entrance draws a bar chart with every bar at zero — a
 * figure showing the wrong value is far worse than one that does not move.
 *
 * Inlined, the page decides. The motion lives in `globals.css` keyed on the
 * `data-part` attributes the generators already emit (see `.figure-chart--motion`),
 * so nothing animates until this component says so, and the resting state of an
 * un-animated chart is the finished drawing. It is played when the figure is
 * adopted, again when it first scrolls into view, and again on hover — a chart
 * whose bars grow out of the axis says "these are quantities" in a way a static
 * picture of the same bars does not.
 *
 * Which is a different threat model from an image, and it is treated as one:
 * an image cannot run a script whatever it contains, an inlined document can, and
 * chart labels come from content files. Everything is parsed and stripped before
 * it is adopted — see `adopt`.
 *
 * Why highlighting at all: a bar chart's job is comparison, and „kiek daugiau
 * pirmadienį negu trečiadienį" means reading two bars against a scale drawn once
 * down the left edge. Singling one out, with its own value stated in words, is the
 * step a child otherwise does by holding a finger on the screen.
 */

interface Part {
  el: SVGElement;
  label: string;
  value: string;
}

/** Only these may survive from the fetched document. Everything here is what a
 *  generator draws with; anything else is not ours.
 *
 *  `style` is deliberately absent. A `<style>` inside inline SVG is a real style
 *  element in the host document — its rules would apply to the whole page, not to
 *  the figure that carried them. The only thing any generator puts there is the
 *  entrance keyframes, and those belong to the page now. */
const ALLOWED = new Set([
  "svg", "g", "defs", "title", "desc",
  "path", "rect", "circle", "ellipse", "line", "polyline", "polygon",
  "text", "tspan", "linearGradient", "radialGradient", "stop", "pattern",
  "clipPath", "mask", "filter", "feDropShadow", "feGaussianBlur", "feOffset",
  "feMerge", "feMergeNode", "feColorMatrix", "feComponentTransfer", "feFuncA",
  "marker", "use", "symbol",
]);

function adopt(markup: string): SVGSVGElement | null {
  const doc = new DOMParser().parseFromString(markup, "image/svg+xml");
  const root = doc.documentElement;
  if (!root || root.nodeName === "parsererror" || root.nodeName.toLowerCase() !== "svg") return null;

  const walk = (node: Element) => {
    for (const child of Array.from(node.children)) {
      if (!ALLOWED.has(child.nodeName)) {
        child.remove();
        continue;
      }
      for (const attribute of Array.from(child.attributes)) {
        // Nothing that can run, and nothing that can fetch: `on*` is script, and
        // a remote reference in a generated figure is always a mistake.
        const name = attribute.name.toLowerCase();
        const value = attribute.value.toLowerCase();
        if (name.startsWith("on") || name === "href" || name === "xlink:href") child.removeAttribute(attribute.name);
        else if (value.includes("javascript:")) child.removeAttribute(attribute.name);
      }
      walk(child);
    }
  };
  walk(root);
  return root as unknown as SVGSVGElement;
}

/** How long the entrance takes, stagger included — see `globals.css`. A hover
 *  arriving inside this window is the pointer settling on the figure, not a
 *  request to see it again, and restarting there is a stutter. */
const MOTION_MS = 900;

/**
 * Markup already fetched this page load, so a chart that is mounted again is
 * drawn *before the browser paints* rather than after a round trip.
 *
 * A question's HTML is rebuilt from scratch whenever anything about it changes —
 * picking an option on a question carrying a `{_}` blank, for one — which hands
 * the chart a new host element and so remounts it. Even served from the browser
 * cache, `fetch` resolves a frame or two later: the figure's box would be empty
 * for those frames and the sentence under it would jump up and back. Cached, the
 * insertion happens in a layout effect and there is no gap at all.
 *
 * Bounded because a session's figures are unbounded and this holds their whole
 * source text; oldest out first.
 */
const MARKUP = new Map<string, string>();
const MARKUP_MAX = 24;

function remember(src: string, markup: string) {
  MARKUP.set(src, markup);
  while (MARKUP.size > MARKUP_MAX) MARKUP.delete(MARKUP.keys().next().value as string);
}

const ZOOM_HINT = "Bakstelėk, kad padidintum";

export function FigureChart({
  src,
  alt,
  className,
  style,
  onPicked,
  inline = false,
  onOpen,
}: {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  /** Reports what is highlighted, so the viewer can say it in words. */
  onPicked?: (part: { label: string; value: string } | null) => void;
  /**
   * In the flow of a question rather than full screen: the chart carries its own
   * magnifier badge and opens the figure when it is activated. Off in the
   * lightbox, where the whole screen is already the figure.
   */
  inline?: boolean;
  onOpen?: () => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const partsRef = useRef<Part[]>([]);
  const playedAt = useRef(0);
  const [failed, setFailed] = useState(false);
  const [index, setIndex] = useState<number | null>(null);

  /**
   * Play the entrance, from the beginning.
   *
   * The class is added and removed on the node rather than rendered, so a
   * re-render (a highlight changing, say) cannot restart it: React only writes
   * `className` when the value it computed differs from the value it computed
   * last time, and this class is never part of that value. The reflow between
   * the two is what makes the second `add` a fresh animation instead of a no-op.
   */
  const play = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    wrap.classList.remove("figure-chart--motion");
    void wrap.offsetWidth;
    wrap.classList.add("figure-chart--motion");
    playedAt.current = performance.now();
  }, []);

  const replay = useCallback(() => {
    if (performance.now() - playedAt.current < MOTION_MS) return;
    play();
  }, [play]);

  const paint = useCallback(
    (next: number | null) => {
      const parts = partsRef.current;
      parts.forEach((part, i) => part.el.classList.toggle("is-picked", i === next));
      wrapRef.current?.classList.toggle("figure-chart--picking", next !== null);
      setIndex(next);
      onPicked?.(next === null ? null : { label: parts[next].label, value: parts[next].value });
    },
    [onPicked]
  );

  /** Parse, sanitize and insert. Everything after the bytes have arrived. */
  const draw = useCallback(
    (markup: string): boolean => {
      const host = hostRef.current;
      if (!host) return false;
      const svg = adopt(markup);
      if (!svg) return false;

      // The drawing decides its own proportions; the box decides how big.
      svg.removeAttribute("width");
      svg.removeAttribute("height");
      svg.setAttribute("class", "figure-chart__svg");
      svg.setAttribute("role", "img");
      svg.setAttribute("aria-label", alt);

      const parts = Array.from(svg.querySelectorAll<SVGElement>("[data-part]"));
      // Position in the series, which is what staggers the entrance: a chart
      // arrives left to right, the order it is read in. Set here rather than in
      // the generator so the `<img>` copy of the same URL is byte-identical.
      parts.forEach((el, i) => el.style.setProperty("--part-index", String(i)));

      host.replaceChildren(svg);
      partsRef.current = parts.map((el) => ({
        el,
        label: el.getAttribute("data-label") || "",
        value: el.getAttribute("data-value") || "",
      }));
      // Started in the same breath as the insertion, so the figure's first
      // painted frame is the animation's first frame — a chart drawn whole and
      // then reset to zero to grow again is a flicker.
      play();
      return true;
    },
    [alt, play]
  );

  // A layout effect, so a cached figure is in the document before the browser
  // paints and a remount costs no visible gap.
  useLayoutEffect(() => {
    let cancelled = false;
    if (!hostRef.current) return;

    const cached = MARKUP.get(src);
    if (cached !== undefined) {
      if (draw(cached)) return;
      // Unparseable from the cache is unparseable from the network too.
      MARKUP.delete(src);
      setFailed(true);
      return;
    }

    (async () => {
      try {
        const response = await fetch(src);
        if (!response.ok) throw new Error(String(response.status));
        const markup = await response.text();
        if (cancelled) return;
        if (!draw(markup)) throw new Error("unparseable");
        remember(src, markup);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [src, draw]);

  /**
   * Played again the first time the figure is actually on screen.
   *
   * A question is scrolled, and a chart three questions down finishes its
   * entrance long before anybody looks at it. This is also the only way a touch
   * user ever sees the motion — there is no hover on a tablet — which is why it
   * is a replay rather than a gate: gating on visibility would leave a figure
   * whose observer never fires sitting at zero, which is the exact defect that
   * kept this animation switched off inside the `<img>`.
   */
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || failed) return;
    let seen = false;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || seen) continue;
          seen = true;
          replay();
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [failed, replay, src]);

  /** Full screen, the chart is the only thing on it, so it takes the focus: the
   *  arrow keys below are attached to this element rather than to the document,
   *  and without focus a keyboard user would have nothing to step through. */
  useEffect(() => {
    if (!inline) wrapRef.current?.focus();
  }, [inline, src]);

  // A pointer picks what it is over. On a touch screen the same handler runs from
  // the tap, which is why the viewer has to know a tap landed on a part — there,
  // a tap normally closes the figure.
  function onMove(e: React.PointerEvent) {
    const parts = partsRef.current;
    if (!parts.length) return;
    const hit = (e.target as Element | null)?.closest?.("[data-part]");
    if (!hit) {
      // Only a pointer that can hover gives up the highlight on leaving. A tap
      // that misses keeps the last one, or every near-miss would clear the answer
      // a child was reading.
      if (e.pointerType === "mouse") paint(null);
      return;
    }
    const next = parts.findIndex((part) => part.el === hit);
    if (next !== -1 && next !== index) paint(next);
  }

  /**
   * Left and right step through the parts, so this works without a pointer.
   *
   * On the element, never on the document. Inline, this chart sits inside a
   * question that owns its own arrow keys — the answer field and the choice
   * buttons — and a chart quietly capturing them at the document would break
   * typing in a field three lines below it.
   */
  function onKeys(e: React.KeyboardEvent) {
    if (inline && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onOpen?.();
      return;
    }
    const parts = partsRef.current;
    if (!parts.length) return;
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    e.stopPropagation();
    const step = e.key === "ArrowRight" ? 1 : -1;
    const from = index === null ? (step === 1 ? -1 : 0) : index;
    paint((from + step + parts.length) % parts.length);
  }

  // A chart that could not be fetched falls back to the picture. Losing the
  // highlight is a small loss; losing the figure in front of a child is not.
  if (failed) {
    return <img src={src} alt={alt} className={className} style={style} draggable={false} />;
  }

  return (
    <div
      ref={wrapRef}
      className={`figure-chart ${inline ? "figure-chart--inline" : ""} ${className ?? ""}`}
      style={style}
      // Hover replays the entrance, and moving between the bars highlights them.
      // Both are instant: a chart that waits before responding reads as a
      // picture that happens to twitch.
      onPointerEnter={(e) => {
        if (e.pointerType === "mouse") replay();
      }}
      onFocus={replay}
      onKeyDown={onKeys}
      {...(inline
        ? {
            role: "button" as const,
            tabIndex: 0,
            title: ZOOM_HINT,
            onClick: onOpen,
          }
        : { tabIndex: 0 })}
    >
      <div
        ref={hostRef}
        className="figure-chart__canvas"
        onPointerMove={onMove}
        onPointerDown={onMove}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") paint(null);
        }}
      />
      {inline && (
        <span className="figure-chart__zoom" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-4.3-4.3M11 8v6M8 11h6" />
          </svg>
        </span>
      )}
    </div>
  );
}

/** Charts whose marks carry a label AND a value, so singling one out has something
 *  to say. A grid figure's cells are named too, but a cell has no value to read —
 *  highlighting one would be decoration. */
const CHARTS = new Set(["bar-chart", "pie-chart"]);

/**
 * Which figures are inlined into the question rather than drawn as an `<img>`.
 *
 * Wider than the highlightable set above, because motion needs no named parts:
 * a line chart and a histogram are data figures read the same way, and they get
 * the entrance without the highlight. Deliberately not "every figure" — every
 * sizing rule in `globals.css` is written against `img`, and each type inlined
 * here has to be given its size back by hand.
 */
const INLINE = new Set([...CHARTS, "line-chart", "histogram"]);

function typeOf(src: string): string | null {
  return /\/svg\/([a-z0-9-]+)/i.exec(src)?.[1]?.toLowerCase() ?? null;
}

export function isChart(src: string): boolean {
  const type = typeOf(src);
  return type ? CHARTS.has(type) : false;
}

export function isInlineChart(src: string): boolean {
  const type = typeOf(src);
  return type ? INLINE.has(type) : false;
}
